import type { MetaChipTone } from "../../../components/UIToolkit/Badges/MetaChip";
import type { EmployeeMatchState } from "../useAttemptsFilter";

/** A state that needs the onboarder's eye. `matched` is deliberately absent — it stays plain default text. */
export type EmployeeMatchAttentionState = Exclude<EmployeeMatchState, "matched">;

/**
 * i18n key for the verdict word shown in the cell itself. Separate from `summaryLabelKey` because the two
 * read differently: the header counts rows ("12 unmatched") while the cell names one row's verdict ("not
 * found"). Covers `matched` too, which the header never counts.
 */
export const EMPLOYEE_MATCH_CELL_LABEL_KEYS: Record<EmployeeMatchState, string> = {
	matched: "onboarding_load.employee_match.matched_cell",
	merged: "onboarding_load.employee_match.merged_cell",
	ambiguous: "onboarding_load.employee_match.ambiguous_cell",
	notFound: "onboarding_load.employee_match.not_found_cell",
};

type EmployeeMatchDisplay = {
	/** Drives the cell chip's fill/ink/border off the theme status palette. */
	tone: MetaChipTone;
	/**
	 * Bare-text color token for the header summary. Mirrors MetaChip's ink (`palette[tone].dark`) so the
	 * header and the cells read as one family, and clears AA on white — `warning.main` is only 3.1:1.
	 */
	textColor: string;
	/** i18n key for how the header summary names this state's row count. */
	summaryLabelKey: string;
	/** i18n key for what the onboarder should do about the row. */
	tooltipKey: string;
};

/** The order the header summary lists the attention states in: the hard failure first, then the verify-me pair. */
export const EMPLOYEE_MATCH_ATTENTION_STATES: readonly EmployeeMatchAttentionState[] = ["notFound", "merged", "ambiguous"];

/**
 * One source of truth for how each attention state is presented, shared by the grid cell and the header
 * summary so the two can never disagree about tone or wording.
 */
export const EMPLOYEE_MATCH_DISPLAY: Record<EmployeeMatchAttentionState, EmployeeMatchDisplay> = {
	notFound: {
		tone: "error",
		textColor: "error.dark",
		summaryLabelKey: "onboarding_load.employee_match.not_found_label",
		tooltipKey: "onboarding_load.employee_match.not_found_tooltip",
	},
	merged: {
		tone: "warning",
		textColor: "warning.dark",
		summaryLabelKey: "onboarding_load.employee_match.merged_label",
		tooltipKey: "onboarding_load.employee_match.merged_tooltip",
	},
	ambiguous: {
		tone: "warning",
		textColor: "warning.dark",
		summaryLabelKey: "onboarding_load.employee_match.ambiguous_label",
		tooltipKey: "onboarding_load.employee_match.ambiguous_tooltip",
	},
};
